'use client';

import type { NextPage } from 'next';
import Image from 'next/image';
import { useForm, Controller } from 'react-hook-form';
import React, { useRef, useState } from 'react';

import styles from './items-form-block1440px.module.scss';

import ButtonSimple from '../HeroBlock/components/ButtonSimple/ButtonSimple';
import TextField from './components/Text-field/Text-field';
import ContactPhoneInput from '../ContactsBlock/components/ContactPhoneInput/ContactPhoneInput';
import {
  ItemsFormBlock1440pxType,
  JetCardFormData,
} from '@/types/itemsFormBlock.types';
import { ErrorModal, SuccessModal } from '@/components';
import TypeJetCardSelect from './components/TypeJetCardSelect/TypeJetCardSelect';
import CheckboxForForm from '../CheckboxForForm/CheckboxForForm';
import { getAttributionPayload } from '@/utils/getAttributionPayload';

const ItemsFormBlockJetCard: NextPage<ItemsFormBlock1440pxType> = ({
  title,
  description,
  dealTitle,
  showCallCta,
  items,
}) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<JetCardFormData>({
    defaultValues: {
      email: '',
      name: '',
      phone: '',
      membershipType: '',
      message: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const onSubmit = async (data: JetCardFormData) => {
    try {
      const attribution = getAttributionPayload(formRef.current);
      const response = await fetch('/api/jetCard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          comment: data.message,
          ...(dealTitle && { dealTitle }),
          ...attribution,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      reset(undefined, {
        keepErrors: false,
        keepDirty: false,
        keepIsSubmitted: false,
        keepTouched: false,
        keepIsValid: false,
        keepSubmitCount: false,
      });
      setSubmitAttempted(false);
      setIsSuccess(true);
    } catch (error) {
      setIsError(true);
      console.error('Error submitting form:', error);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    handleSubmit(onSubmit)(e);
  };

  const filterCyrillicFromEmail = (value: string) => {
    return value.replace(/[а-яё]/gi, '');
  };

  return (
    <>
      <section className={styles.wrapper} id="form">
        <div className="container">
          <div className={`${styles.formblock}`}>
            <div className={styles.leftcol}>
              <div className={styles.text}>
                {title && (
                  <h2
                    className={styles.lookingForA}
                    dangerouslySetInnerHTML={{ __html: title }}
                  />
                )}
                {description && (
                  <div
                    className={styles.tellUsWhatContainer}
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                )}
              </div>
              <div className={styles.items}>
                {items &&
                  items.map((item) => (
                    <div className={styles.item11} key={item.id}>
                      <Image
                        className={styles.icon5}
                        width={24}
                        height={24}
                        sizes="100vw"
                        alt=""
                        src="/img/itemsFormBlock/round-check.svg"
                      />
                      <div className={styles.text6}>{item.title}</div>
                    </div>
                  ))}
              </div>
            </div>
            <div className={styles.rightcol}>
              <form
                ref={formRef}
                onSubmit={handleFormSubmit}
                noValidate
                className={styles.form}
              >
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: 'Name is required',
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      label="Your name"
                      placeholder="Enter your name"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                    />
                  )}
                />

                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: 'Invalid email format',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      label="Your email"
                      placeholder="Enter your email address"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(filterCyrillicFromEmail(e.target.value))
                      }
                      error={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const v = (value || '').trim();
                      if (!v || v === '+')
                        return 'Please enter your phone number';
                      const digits = v.replace(/\D/g, '');
                      if (digits.length < 6)
                        return 'Please enter a complete phone number';
                      if (digits.length > 19) return 'Phone number is too long';
                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => {
                    const shouldShowError = submitAttempted && fieldState.error;
                    return (
                      <ContactPhoneInput
                        label="Your phone"
                        placeholder="Enter your phone"
                        value={field.value || ''}
                        onChange={(phone) => {
                          field.onChange(phone);
                        }}
                        error={
                          shouldShowError
                            ? fieldState.error?.message
                            : undefined
                        }
                      />
                    );
                  }}
                />

                <Controller
                  name="membershipType"
                  control={control}
                  rules={{
                    required: 'Type of membership is required',
                  }}
                  render={({ field, fieldState }) => (
                    <div>
                      <TypeJetCardSelect
                        label="Type of membership"
                        value={field.value}
                        onChange={field.onChange}
                        // icon="/img/heroBlock/form/aircraft.svg"
                      />
                      {fieldState.error && (
                        <div className={styles.errorMessage}>
                          {fieldState.error.message}
                        </div>
                      )}
                    </div>
                  )}
                />

                <div className={styles.textField}>
                  <div className={styles.label}>Your message</div>
                  <Controller
                    name="message"
                    control={control}
                    rules={{
                      required: 'Message is required',
                      minLength: {
                        value: 4,
                        message: 'Message should be at least 4 characters',
                      },
                      maxLength: {
                        value: 1000,
                        message: 'Message should not exceed 1000 characters',
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <>
                        <textarea
                          className={`${styles.row} ${fieldState.error ? styles.error : ''}`}
                          placeholder="Enter your message (any questions or wishes)"
                          rows={4}
                          cols={32}
                          value={field.value}
                          onChange={field.onChange}
                        />
                        {fieldState.error && (
                          <div className={styles.errorText}>
                            {fieldState.error.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
                <CheckboxForForm
                  consentChecked={consentChecked}
                  setConsentChecked={setConsentChecked}
                />
                <div className={styles.submitButton}>
                  <ButtonSimple
                    text={isSubmitting ? 'Sending...' : 'Send'}
                    designBlendingMode=""
                    designBlendingMode1=""
                    type="submit"
                    disabled={isSubmitting}
                  />
                  {showCallCta && (
                    <a href="tel:18335934372" className={styles.callCta}>
                      Or call us: 1-833-FLY-HERA
                    </a>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {isSuccess && <SuccessModal onClose={() => setIsSuccess(false)} />}
      {isError && <ErrorModal onClose={() => setIsError(false)} />}
    </>
  );
};

export default ItemsFormBlockJetCard;
